import { Link, router } from "@inertiajs/react"
import { Trans, useLingui } from "@lingui/react/macro"
import type {
  DealDrawerAiPause,
  DealDrawerAiPauseAction,
} from "@/types/v2/conversation"
import { relativeTimeLabel } from "@/lib/conversation-formatting"

// AI PAUSED FOR YOUR CALL banner (Paper 7DD-0 / IR1-0 /
// JUB-0 / ENU-0 / FL1-0 / GAE-0 / JE7-0). Cream/amber
// callout that surfaces above the History feed whenever
// the AI has paused itself on this deal — the call-to-
// action is a human review.
//
// The mobile variant (Paper 97K-0 / KEZ-0) renders the
// same banner inline; the only difference is the action
// row wraps when there's not enough horizontal room. The
// `compact` prop trims the body padding for the mobile
// hero where the banner sits flush against the stage
// strip.

interface AiPausedBannerProps {
  aiPause: DealDrawerAiPause
  // Deal id is required to fire the Resume AI link.
  // Optional today so call sites that haven't been
  // updated yet keep working — Resume AI just hides
  // until they wire the prop through.
  deal?: { id: number }
  compact?: boolean
  className?: string
}

export function AiPausedBanner({
  aiPause,
  deal,
  compact = false,
  className,
}: AiPausedBannerProps) {
  const { t } = useLingui()
  const padClass = compact ? "py-3 px-3.5" : "py-4 px-4.5"
  const wrapperClass = [
    "flex flex-col rounded-xl gap-3",
    "bg-[#FAF1E6] border border-[#EBD9C2]",
    padClass,
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ")

  const resumeAi = () => {
    if (!deal) return
    router.delete(`/v2/deals/${deal.id}/ai_pause`, {
      preserveScroll: true,
    })
  }

  return (
    <section
      aria-label={t`AI paused for your call`}
      data-testid="ai-paused-banner"
      className={wrapperClass}
    >
      <header className="flex items-center gap-2">
        <SparkleIcon />
        <span className="tracking-[0.06em] uppercase font-semibold text-brand text-[11px] leading-[14px]">
          <Trans>AI paused for your call</Trans>
        </span>
        <span className="grow" />
        <time
          dateTime={aiPause.pausedAtIso}
          className="text-[#9A938A] text-[11px] leading-[14px]"
        >
          {relativeTimeLabel(aiPause.pausedAtIso)}
        </time>
        {deal ? (
          <button
            type="button"
            onClick={resumeAi}
            data-testid="ai-paused-resume"
            className="text-brand text-[11px] leading-[14px] font-medium hover:underline"
          >
            <Trans>Resume AI</Trans>
          </button>
        ) : null}
      </header>
      <p className="text-foreground text-sm leading-[21px]">
        {aiPause.summary}
      </p>
      <div className="flex flex-wrap gap-2">
        {aiPause.actions.map((action, idx) => (
          <ActionButton key={`${action.label}-${idx}`} action={action} />
        ))}
      </div>
    </section>
  )
}

function ActionButton({ action }: { action: DealDrawerAiPauseAction }) {
  const baseClass =
    "rounded-full py-1.75 px-3.5 font-medium text-xs leading-4"
  const styleClass =
    action.style === "primary"
      ? "bg-[#1E1B17] text-[#F0EDE6] hover:bg-black"
      : "bg-white border border-[#C9C2B6] text-foreground hover:bg-[#F7F5F0]"

  const className = `${baseClass} ${styleClass}`

  if (action.href) {
    return (
      <Link href={action.href} className={className}>
        {action.label}
      </Link>
    )
  }
  return (
    <button type="button" className={className} disabled>
      {action.label}
    </button>
  )
}

function SparkleIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M8 1C8 4 9 7 12 7C15 7 8 8 8 8C8 8 15 9 12 9C9 9 8 12 8 15C8 12 7 9 4 9C1 9 8 8 8 8C8 8 1 7 4 7C7 7 8 4 8 1Z"
        fill="#B86A2D"
      />
    </svg>
  )
}
